import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  FormStateUpdate,
  FormStates,
  GetProductRequestParams,
  OperationType,
  PriceCalculationRequestParams,
  PriceCalculationResult,
  PriceCalculationState,
  StoredAdviceCartStartPayload,
} from "Store/priceCalculation/priceCalculationInterfaces";

import { Cart } from "Store/cart/cartInterfaces";
import { ErrorStateOld } from "Store/interfaces/products";
import { InsuranceProductPriceRequestInfo } from "Store/interfaces/products/checkPrice";
import { InsuranceProduct } from "Store/interfaces/products/checkout";

const initialState: PriceCalculationState = {
  priceCalculationFormStates: {},
  priceCalculationRequest: { numberOfProductsInCart: 0 },
  priceCalculationResult: {
    current: null,
    isLoading: false,
    error: null,
  },
  priceCalculationResults: {
    current: null,
    isLoading: false,
    error: null,
  },
  storedProduct: {
    current: null,
    isLoading: false,
    error: null,
  },
  storedAdviceCart: {
    current: null,
    isLoading: false,
    error: null,
  },
};

const priceCalculationSlice = createSlice({
  name: "priceCalculation",
  initialState,
  reducers: {
    initializePriceCalculationFormState: (
      state,
      action: PayloadAction<FormStateUpdate>,
    ) => {
      const { stepId, formState } = action.payload;
      state.priceCalculationFormStates[stepId] = formState;
    },

    updatePriceCalculationFormState: (
      state,
      action: PayloadAction<FormStateUpdate>,
    ) => {
      const { stepId, formState } = action.payload;
      // Casting to Record<string, unknown> because we are mixing two "potentially" different form states
      // Creates a type error because we have conflicting definitions of coverageAmount (number | string) in multiple form states
      // TODO: Fix this in a better way
      const currentFormState = state.priceCalculationFormStates[
        stepId
      ] as Record<string, unknown>;
      const formStateUpdate = formState as Record<string, unknown>;
      state.priceCalculationFormStates[stepId] = {
        ...currentFormState,
        ...formStateUpdate,
      };
    },

    resetPriceCalculationFormState: (state, action: PayloadAction<string>) => {
      const { payload: stepId } = action;
      delete state.priceCalculationFormStates[stepId];
    },

    updatePriceCalculationFormStates: (
      state,
      action: PayloadAction<FormStates>,
    ) => {
      state.priceCalculationFormStates = action.payload;
    },

    editPriceCalculationRequest: (
      state,
      action: PayloadAction<InsuranceProductPriceRequestInfo>,
    ) => {
      state.priceCalculationRequest = {
        ...state.priceCalculationRequest,
        productInfo: {
          ...state.priceCalculationRequest?.productInfo,
          ...action.payload,
        },
      };
    },

    clearPriceCalculationResults: (state) => {
      state.priceCalculationResult.current = {};
      // state.storedProduct.current = null; //TODO SSX-476: Need to look more into this bug. Commenting this line out for now.
    },

    sendPriceCalculationRequestsStart: (
      state,
      _action: PayloadAction<PriceCalculationRequestParams[]>,
    ) => {
      state.priceCalculationResults.isLoading = true;
      state.priceCalculationResult.isLoading = true;
      state.priceCalculationResults.error = null;
    },

    sendPriceCalculationRequestStart: (
      state,
      _action: PayloadAction<PriceCalculationRequestParams>,
    ) => {
      state.priceCalculationResult.isLoading = true;
    },

    sendPriceCalculationRequestSucceeded: (
      state,
      action: PayloadAction<PriceCalculationResult>,
    ) => {
      state.priceCalculationResult.current = {
        ...state.priceCalculationResult.current,
        ...action.payload,
      };
      state.priceCalculationResult.isLoading = false;
      state.priceCalculationResult.error = null;
    },

    sendPriceCalculationRequestWithCompositesSucceeded: (
      state,
      action: PayloadAction<PriceCalculationResult>,
    ) => {
      state.priceCalculationResult.current = {
        ...state.priceCalculationResult.current,
        ...action.payload,
        composites: {
          ...state.priceCalculationResult.current?.composites,
          ...action.payload.composites,
        },
      };
      state.priceCalculationResult.isLoading = false;
      state.priceCalculationResult.error = null;
    },

    sendPriceCalculationRequestsFinished: (state) => {
      state.priceCalculationResults.isLoading = false;
      state.priceCalculationResult.isLoading = false;
    },

    operationFailed: (
      state,
      action: PayloadAction<ErrorStateOld<OperationType>>,
    ) => {
      state.priceCalculationResult.error = action.payload;
      state.priceCalculationResults.error = action.payload;
      state.priceCalculationResult.isLoading = false;
    },

    getStoredProductStart: (
      state,
      _action: PayloadAction<GetProductRequestParams>,
    ) => {
      state.storedProduct.isLoading = true;
    },

    getStoredProductSucceeded: (
      state,
      action: PayloadAction<InsuranceProduct>,
    ) => {
      state.storedProduct.current = action.payload;
      state.storedProduct.isLoading = false;
    },

    storedProductOperationFailed: (
      state,
      action: PayloadAction<ErrorStateOld<OperationType>>,
    ) => {
      state.storedProduct.error = action.payload;
      state.storedProduct.isLoading = false;
    },

    getStoredAdviceCartStart: (
      state,
      _action: PayloadAction<StoredAdviceCartStartPayload>,
    ) => {
      state.storedAdviceCart.isLoading = true;
    },

    getStoredAdviceCartSucceeded: (state, action: PayloadAction<Cart>) => {
      state.storedAdviceCart.current = action.payload;
      state.storedAdviceCart.isLoading = false;
    },

    storedAdviceCartOperationFailed: (
      state,
      action: PayloadAction<ErrorStateOld<OperationType>>,
    ) => {
      state.storedAdviceCart.error = action.payload;
      state.storedAdviceCart.isLoading = false;
    },

    clearPriceCalculationState: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const {
  clearPriceCalculationResults,
  editPriceCalculationRequest,
  getStoredProductStart,
  getStoredProductSucceeded,
  storedProductOperationFailed,
  getStoredAdviceCartStart,
  getStoredAdviceCartSucceeded,
  storedAdviceCartOperationFailed,
  initializePriceCalculationFormState,
  resetPriceCalculationFormState,
  operationFailed,
  sendPriceCalculationRequestStart,
  sendPriceCalculationRequestsStart,
  sendPriceCalculationRequestsFinished,
  sendPriceCalculationRequestSucceeded,
  sendPriceCalculationRequestWithCompositesSucceeded,
  updatePriceCalculationFormState,
  updatePriceCalculationFormStates,
  clearPriceCalculationState,
} = priceCalculationSlice.actions;
export const priceCalculation = priceCalculationSlice.reducer;
